import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, FilePlus2, Loader2, MessageSquarePlus, RefreshCcw, RotateCcw, Tag, XCircle, ListRestart } from "lucide-react";
import { toast } from "sonner";
import { sumsubHub } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const KINDS = {
  approve: { title: "Approve applicant", icon: CheckCircle2, verb: "Approve", tone: "bg-emerald-600 hover:bg-emerald-700",
    blurb: "Sets the review to completed · GREEN without changing any step. Use when a document was wrongly rejected by the automatic checks.", note: "Internal note (optional)" },
  reject: { title: "Reject applicant", icon: XCircle, verb: "Reject", tone: "bg-red-600 hover:bg-red-700", noteRequired: true,
    blurb: "Sets the review to completed · RED · FINAL. Sumsub records it as an external decision — the applicant cannot retry on this level.", note: "Why? (required — stays internal)" },
  reset: { title: "Reset the whole applicant", icon: RotateCcw, verb: "Reset everything", tone: "bg-amber-600 hover:bg-amber-700",
    blurb: "Deactivates every uploaded document and clears all step statuses — the applicant starts the journey again from scratch. Not allowed while a check is pending.", note: null },
  reset_step: { title: "Reset one verification step", icon: ListRestart, verb: "Reset step", tone: "bg-amber-600 hover:bg-amber-700",
    blurb: "Only this step is cleared; the applicant re-does it on the same link. Everything else they completed stays.", note: null, step: true },
  request_recheck: { title: "Request a re-check", icon: RefreshCcw, verb: "Send to pending", tone: "bg-sky-600 hover:bg-sky-700",
    blurb: "Moves the applicant to pending so Sumsub reviews the current documents again (not allowed while already pending / queued).", note: "Reason (optional)" },
  request_docs: { title: "Request missing documents", icon: FilePlus2, verb: "Request", tone: "bg-sky-600 hover:bg-sky-700",
    blurb: "Re-opens the journey for an on-hold applicant so they can add what is missing; completed steps are kept.", note: "Message for the record (optional)" },
  tags: { title: "Tags", icon: Tag, verb: "Save tags", tone: "bg-slate-800 hover:bg-slate-900", blurb: "Comma-separated. Existing tags can be ticked off to remove them.", note: null, tags: true },
  note: { title: "Add a note", icon: MessageSquarePlus, verb: "Add note", tone: "bg-slate-800 hover:bg-slate-900", noteRequired: true,
    blurb: "Visible to the team in Sumsub (your name is appended).", note: "Note" },
};

export function ActionDialog({ kind, applicant, steps, onClose, onDone }) {
  const cfg = KINDS[kind];
  const [note, setNote] = useState("");
  const [step, setStep] = useState(steps?.find((s) => s.image_ids?.length)?.key || "IDENTITY");
  const [addTags, setAddTags] = useState("");
  const [remove, setRemove] = useState(new Set());
  const [busy, setBusy] = useState(false);
  if (!cfg) return null;
  const Icon = cfg.icon;
  const submit = async () => {
    setBusy(true);
    try {
      let res;
      if (kind === "reset_step") res = await sumsubHub.act(applicant.id, "reset-step", { step });
      else if (kind === "request_recheck") res = await sumsubHub.act(applicant.id, "request-recheck", { reason: note });
      else if (kind === "request_docs") res = await sumsubHub.act(applicant.id, "request-docs", { note });
      else if (kind === "tags") res = await sumsubHub.act(applicant.id, "tags", { add: addTags.split(",").map((t) => t.trim()).filter(Boolean), remove: [...remove] });
      else if (kind === "note") res = await sumsubHub.act(applicant.id, "notes", { note });
      else res = await sumsubHub.act(applicant.id, kind, { note });
      toast.success(`${cfg.title} — done`);
      onDone(res);
      onClose();
    } catch (e) {
      toast.error(errorDetailMessage(e, e.message), { duration: 8000 });
    } finally { setBusy(false); }
  };
  const disabled = busy || (cfg.noteRequired && !note.trim()) || (kind === "tags" && !addTags.trim() && !remove.size);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`sumsub-action-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="w-5 h-5" />{cfg.title}</DialogTitle>
          <DialogDescription>{applicant.first_name} {applicant.last_name} · <span className="font-mono text-xs">{applicant.id}</span></DialogDescription>
        </DialogHeader>
        <p className="rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">{cfg.blurb}</p>
        {cfg.step && (
          <Select value={step} onValueChange={setStep}>
            <SelectTrigger data-testid="sumsub-reset-step-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(steps || []).filter((s) => s.key !== "APPLICANT_DATA" || true).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}{s.answer ? ` · ${s.answer}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {cfg.tags && (
          <div className="space-y-2">
            <Input value={addTags} onChange={(e) => setAddTags(e.target.value)} placeholder="new-tag, another-tag" data-testid="sumsub-tags-input" />
            {applicant.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {applicant.tags.map((t) => (
                  <button key={t} type="button" onClick={() => setRemove((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; })} data-testid={`sumsub-tag-toggle-${t}`}>
                    <Badge variant="outline" className={`text-[10px] ${remove.has(t) ? "line-through bg-rose-50 text-rose-700 border-rose-300" : ""}`}>{t}</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {cfg.note && <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={cfg.note} data-testid="sumsub-action-note" />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="sumsub-action-cancel">Cancel</Button>
          <Button className={`text-white ${cfg.tone}`} onClick={submit} disabled={disabled} data-testid="sumsub-action-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Icon className="w-4 h-4 mr-1" />}{cfg.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ActionBar({ canAct, onPick }) {
  const items = [["approve", "Approve"], ["reject", "Reject"], ["request_recheck", "Re-check"], ["request_docs", "Request docs"], ["reset_step", "Reset step"], ["reset", "Reset all"], ["tags", "Tags"], ["note", "Note"]];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="sumsub-action-bar">
      {items.map(([k, label]) => {
        const Icon = KINDS[k].icon;
        return (
          <Button key={k} size="sm" variant="outline" className="h-7 text-xs" disabled={!canAct} title={canAct ? KINDS[k].title : "Underwriting permission needed"} onClick={() => onPick(k)} data-testid={`sumsub-act-${k}`}>
            <Icon className={`w-3.5 h-3.5 mr-1 ${k === "approve" ? "text-emerald-600" : k === "reject" ? "text-red-600" : k.startsWith("reset") ? "text-amber-600" : "text-slate-500"}`} />{label}
          </Button>
        );
      })}
    </div>
  );
}
